import type { FC } from "react";
import { cx } from "@/utils/cx";

/**
 * SegmentedControl — přepínač několika vzájemně výlučných voleb v UUI stylu
 * (kontejner `bg-secondary`, aktivní segment `bg-primary shadow-xs`).
 *
 * Vznikl sloučením `ThemeToggle` a tehdejšího `LocaleToggle`, které byly řádek po
 * řádku totéž. Jazyk se od té doby přesunul do rozbalovacího seznamu
 * ({@link LocaleSelect}), takže dnes na tomhle stojí jen volba motivu — vzhled
 * segmentů ale zůstává na jednom místě pro každý další přepínač.
 *
 * Vědomě to NENÍ react-aria komponenta: jde o okamžitou akci (přepnutí motivu
 * / jazyka), ne o hodnotu formuláře. `aria-pressed` na tlačítku v roli `group`
 * je pro odečítač obrazovky přesnější než radiogroup, kterou nikdo neodesílá.
 *
 * `pages/settings/AppearancePage.tsx` na tomhle záměrně nestojí — tam je motiv
 * prezentovaný jako karty s náhledem, což je jiný vzor, ne varianta téhož.
 */
export interface SegmentedOption<T extends string> {
  value: T;
  /** Text v segmentu i v `title` — jeden zdroj pravdy. */
  label: string;
  /** Delší popis do `title`, když je `label` jen zkratka (CS / SK / EN). */
  title?: string;
  icon?: FC<{ className?: string }>;
}

interface SegmentedControlProps<T extends string> {
  options: SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Popisek skupiny pro odečítač obrazovky. */
  ariaLabel: string;
  /** `sm` = kompaktní (horní lišta), `md` = výchozí (patička sidebaru, menu účtu). */
  size?: "sm" | "md";
  isDisabled?: boolean;
  className?: string;
}

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  size = "md",
  isDisabled,
  className,
}: SegmentedControlProps<T>) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={cx("flex gap-0.5 rounded-lg bg-secondary p-0.5", className)}
    >
      {options.map(({ value: optionValue, label, title, icon: Icon }) => {
        const active = value === optionValue;
        return (
          <button
            key={optionValue}
            type="button"
            aria-pressed={active}
            title={title ?? label}
            disabled={isDisabled}
            onClick={() => onChange(optionValue)}
            className={cx(
              "flex flex-1 items-center justify-center gap-1.5 rounded-md font-medium transition disabled:opacity-60",
              size === "sm" ? "px-1.5 py-1 text-xs" : "px-2 py-1.5 text-xs",
              active ? "bg-primary text-primary shadow-xs" : "text-quaternary hover:text-secondary",
            )}
          >
            {Icon && <Icon className="size-4 shrink-0" aria-hidden="true" />}
            <span>{label}</span>
          </button>
        );
      })}
    </div>
  );
}
